// InvoiceStatementRowMenu.jsx — per-row ▾ action menu for the Invoice Statements table.
// Mirrors ReconcileDropDownMenu: portal to body, fixed positioning, close-others event,
// outside-click + Escape to close. Actions: Jump to reconcile / View statement / Download / Delete.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";

const CLOSE_EVENT = "invstatement-rowmenu-close-others";

export default function InvoiceStatementRowMenu({ rowKey, items }) {
   const [isOpen, setIsOpen] = useState(false);
   const buttonRef = useRef(null);
   const menuRef = useRef(null);
   const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
   const dropdownId = `invstatement-actions-${rowKey ?? "row"}`;

   const updateMenuPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = menuRef.current?.offsetWidth || 190;
      const maxLeft = Math.max(8, window.innerWidth - menuWidth - 8);
      const left = Math.min(Math.max(8, rect.right - menuWidth), maxLeft);
      setMenuPos({ top: rect.bottom + 4, left });
   };

   useEffect(() => {
      const handleCloseOthers = (e) => { if (e.detail !== dropdownId) setIsOpen(false); };
      document.addEventListener(CLOSE_EVENT, handleCloseOthers);
      return () => document.removeEventListener(CLOSE_EVENT, handleCloseOthers);
   }, [dropdownId]);

   useEffect(() => {
      if (!isOpen) return;
      const rafId = requestAnimationFrame(updateMenuPosition);
      const onViewportChange = () => updateMenuPosition();
      const onOutsideMouseDown = (e) => {
         if (buttonRef.current?.contains(e.target)) return;
         if (menuRef.current?.contains(e.target)) return;
         setIsOpen(false);
      };
      const onEscape = (e) => { if (e.key === "Escape") setIsOpen(false); };
      window.addEventListener("resize", onViewportChange);
      window.addEventListener("scroll", onViewportChange, true);
      document.addEventListener("mousedown", onOutsideMouseDown);
      document.addEventListener("keydown", onEscape);
      return () => {
         cancelAnimationFrame(rafId);
         window.removeEventListener("resize", onViewportChange);
         window.removeEventListener("scroll", onViewportChange, true);
         document.removeEventListener("mousedown", onOutsideMouseDown);
         document.removeEventListener("keydown", onEscape);
      };
   }, [isOpen]);

   return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
         <button
            ref={buttonRef}
            type="button"
            className="btn btn-link p-0 border-0"
            id={dropdownId}
            aria-expanded={isOpen}
            onMouseDown={(e) => {
               e.preventDefault();
               e.stopPropagation();
               document.dispatchEvent(new CustomEvent(CLOSE_EVENT, { detail: dropdownId }));
               if (!isOpen) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const estMenuWidth = menuRef.current?.offsetWidth || 190;
                  const maxLeft = Math.max(8, window.innerWidth - estMenuWidth - 8);
                  const left = Math.min(Math.max(8, rect.right - estMenuWidth), maxLeft);
                  setMenuPos({ top: rect.bottom + 4, left });
               }
               setIsOpen((prev) => !prev);
            }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={{ cursor: "pointer", boxShadow: "none" }}
         >
            <FaChevronDown />
         </button>
         {isOpen &&
            createPortal(
               <ul
                  ref={menuRef}
                  className="dropdown-menu show"
                  aria-labelledby={dropdownId}
                  style={{ position: "fixed", top: `${menuPos.top}px`, left: `${menuPos.left}px`, display: "block", zIndex: 3000, minWidth: "190px" }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
               >
                  {items.map((item, index) => (
                     <li key={`ml-${index}`}>
                        <button
                           type="button"
                           className="dropdown-item"
                           disabled={item.disabled}
                           onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsOpen(false);
                              item.onSelect?.();
                           }}
                        >
                           {item.label}
                        </button>
                     </li>
                  ))}
               </ul>,
               document.body
            )}
      </div>
   );
}
